import { SITE_NAME, SITE_URL } from './site';

export type StringToolSlug =
  | 'uppercase'
  | 'lowercase'
  | 'title-case'
  | 'sentence-case'
  | 'camel-case'
  | 'pascal-case'
  | 'snake-case'
  | 'kebab-case'
  | 'character-counter'
  | 'reverse-text'
  | 'trim-whitespace'
  | 'remove-spaces'
  | 'text-compare';

export type StringToolCategory = 'case' | 'analyze' | 'transform' | 'compare';

export interface StringToolRoute {
  slug: StringToolSlug;
  category: StringToolCategory;
  label: string;
  shortLabel: string;
  title: string;
  description: string;
}

export const STRING_TOOL_CATEGORY_LABELS: Record<StringToolCategory, string> = {
  case: 'Case converters',
  analyze: 'Counters',
  transform: 'Text cleanup',
  compare: 'Compare',
};

export const STRING_TOOL_ROUTES: StringToolRoute[] = [
  {
    slug: 'uppercase',
    category: 'case',
    label: 'Uppercase Converter',
    shortLabel: 'Uppercase',
    title: 'Uppercase Text Converter Online',
    description:
      'Convert text to UPPERCASE instantly for headings, labels, filenames, and copy cleanup. Free online uppercase converter with live preview.',
  },
  {
    slug: 'lowercase',
    category: 'case',
    label: 'Lowercase Converter',
    shortLabel: 'Lowercase',
    title: 'Lowercase Text Converter Online',
    description:
      'Convert text to lowercase online for clean copy, URLs, tags, and consistent formatting. Fast, free, and no signup required.',
  },
  {
    slug: 'title-case',
    category: 'case',
    label: 'Title Case Converter',
    shortLabel: 'Title case',
    title: 'Title Case Converter Online',
    description:
      'Convert text to Title Case online for headlines, blog titles, product names, and polished copy in one click.',
  },
  {
    slug: 'sentence-case',
    category: 'case',
    label: 'Sentence Case Converter',
    shortLabel: 'Sentence case',
    title: 'Sentence Case Converter Online',
    description:
      'Convert text to sentence case online — capitalize the first letter and lower the rest for natural, readable sentences.',
  },
  {
    slug: 'camel-case',
    category: 'case',
    label: 'Camel Case Converter',
    shortLabel: 'Camel case',
    title: 'Camel Case Converter Online',
    description:
      'Turn text into camelCase online for variables, keys, IDs, and developer-friendly naming in seconds.',
  },
  {
    slug: 'pascal-case',
    category: 'case',
    label: 'Pascal Case Converter',
    shortLabel: 'Pascal case',
    title: 'Pascal Case Converter Online',
    description:
      'Convert text to PascalCase online for class names, React components, types, and clean technical naming.',
  },
  {
    slug: 'snake-case',
    category: 'case',
    label: 'Snake Case Converter',
    shortLabel: 'Snake case',
    title: 'Snake Case Converter Online',
    description:
      'Convert text to snake_case online for database columns, constants, file names, and backend-friendly identifiers.',
  },
  {
    slug: 'kebab-case',
    category: 'case',
    label: 'Kebab Case Converter',
    shortLabel: 'Kebab case',
    title: 'Kebab Case Converter Online',
    description:
      'Convert text to kebab-case online for URL slugs, CSS classes, HTML attributes, and SEO-friendly paths.',
  },
  {
    slug: 'character-counter',
    category: 'analyze',
    label: 'Character & Word Counter',
    shortLabel: 'Counter',
    title: 'Character & Word Counter Online',
    description:
      'Count characters, words, and lines online with instant stats for copy limits, SEO meta descriptions, and validation.',
  },
  {
    slug: 'reverse-text',
    category: 'transform',
    label: 'Reverse Text',
    shortLabel: 'Reverse',
    title: 'Reverse Text Online',
    description:
      'Reverse a string online character by character. Flip text instantly for puzzles, testing, or quick string manipulation.',
  },
  {
    slug: 'trim-whitespace',
    category: 'transform',
    label: 'Trim Whitespace',
    shortLabel: 'Trim',
    title: 'Trim Whitespace Online',
    description:
      'Remove leading and trailing whitespace from text online. Clean pasted copy, form inputs, and CSV values in one step.',
  },
  {
    slug: 'remove-spaces',
    category: 'transform',
    label: 'Remove Spaces',
    shortLabel: 'No spaces',
    title: 'Remove Spaces From Text Online',
    description:
      'Strip all spaces from text online to build compact strings, IDs, tokens, or normalized values without manual editing.',
  },
  {
    slug: 'text-compare',
    category: 'compare',
    label: 'Text Compare',
    shortLabel: 'Compare',
    title: 'Text Compare & Character Diff Online',
    description:
      'Compare two strings side by side online. Highlight mismatched characters by position and see the total unmatched count.',
  },
];

/** Legacy slugs from the first release — redirect to renamed routes. */
export const LEGACY_STRING_TOOL_SLUGS: Record<string, StringToolSlug> = {
  'string-length': 'character-counter',
  'string-diff': 'text-compare',
};

export function isStringToolSlug(value: string): value is StringToolSlug {
  return STRING_TOOL_ROUTES.some((route) => route.slug === value);
}

export function resolveStringToolSlug(slug: string | undefined): StringToolSlug | null {
  if (!slug) {
    return null;
  }

  if (isStringToolSlug(slug)) {
    return slug;
  }

  return LEGACY_STRING_TOOL_SLUGS[slug] ?? null;
}

export function getStringToolRoute(slug: string | undefined): StringToolRoute | null {
  const resolved = resolveStringToolSlug(slug);
  if (!resolved) {
    return null;
  }

  return STRING_TOOL_ROUTES.find((route) => route.slug === resolved) ?? null;
}

export function getStringToolPath(slug: StringToolSlug): string {
  return `/string-tools/${slug}`;
}

export function getStringToolsByCategory(category: StringToolCategory): StringToolRoute[] {
  return STRING_TOOL_ROUTES.filter((route) => route.category === category);
}

export function getStringToolStructuredData(route: StringToolRoute) {
  const path = getStringToolPath(route.slug);
  const url = `${SITE_URL}${path}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: route.title,
      applicationCategory: 'UtilityApplication',
      operatingSystem: 'Any',
      url,
      description: route.description,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: SITE_NAME,
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: route.shortLabel,
          item: url,
        },
      ],
    },
  ];
}
